// src/PatientPortalHub/PatientMap.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";

import { auth, db, storage } from "../firebase";
import { loadGoogleMaps } from "../mapLoader";
import MapView from "../components/patient/Home/MapView";
import SearchFilters from "../components/patient/Home/SearchFilters";
import LocationStatus from "../components/patient/Home/LocationStatus";
import { makeDoctorAvatar } from "../utils/images";
import { ArrowLeft, MapPin } from "lucide-react";

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function PatientMap() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  // state
  const [doctors, setDoctors] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");

  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 41.3275,
    lng: 19.8187,
  });
  const [nearbyRadius, setNearbyRadius] = useState(25);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const [followedDoctors, setFollowedDoctors] = useState({});
  const [followLoading, setFollowLoading] = useState({});

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  /* ---------- load Google Maps script (hook always called) ---------- */
  useEffect(() => {
    let alive = true;

    if (!GOOGLE_MAPS_API_KEY) {
      setError("Google Maps API key nuk është konfiguruar.");
      return;
    }

    loadGoogleMaps(GOOGLE_MAPS_API_KEY).then((ok) => {
      if (!alive) return;
      if (!ok) {
        setError("Nuk mund të ngarkojmë Google Maps.");
        return;
      }
      setIsMapLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [GOOGLE_MAPS_API_KEY]);

  /* ---------- get user location (hook always called, condition inside) ---------- */
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Shfletuesi yt nuk e mbështet gjeolokalizimin.");
      return;
    }
    setIsGettingLocation(true);
    setLocationError(null);

    const timeoutId = setTimeout(() => {
      setLocationError(
        "Kërkesa për vendndodhjen skadoi. Po përdorim vendndodhjen e parazgjedhur."
      );
      setIsGettingLocation(false);
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(loc);
        setMapCenter(loc);
        setIsGettingLocation(false);
      },
      (error) => {
        clearTimeout(timeoutId);
        let msg = "Nuk mund të marrim vendndodhjen.";
        if (error.code === error.PERMISSION_DENIED)
          msg = "Lejo aksesin e vendndodhjes nga shfletuesi.";
        else if (error.code === error.POSITION_UNAVAILABLE)
          msg = "Vendndodhja nuk është e disponueshme.";
        else if (error.code === error.TIMEOUT)
          msg = "Kërkesa për vendndodhje skadoi.";
        setLocationError(msg);
        setIsGettingLocation(false);
      },
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 300000 }
    );
  }, []);

  // try location once on mount
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  /* ---------- fetch doctors (hook always called, uses userLocation) ---------- */
  const fetchDoctors = useCallback(async () => {
    try {
      setMapLoading(true);
      setError(null);

      const snap = await getDocs(collection(db, "doctors"));
      if (snap.empty) {
        setDoctors([]);
        setMapLoading(false);
        return;
      }

      const rows = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const coords = data?.location?.coordinates;
          if (!coords?.lat || !coords?.lng) return null;

          let photoUrl =
            data.photoURL || data.photoUrl || data.profilePicture;

          if (!photoUrl) {
            try {
              const folderRef = ref(storage, `doctor_photos/${docSnap.id}`);
              const files = await listAll(folderRef);
              if (files.items.length > 0) {
                photoUrl = await getDownloadURL(files.items[0]);
              }
            } catch {
              // ignore
            }
          }

          if (!photoUrl) {
            photoUrl = makeDoctorAvatar(data?.name || "Doctor");
          }

          let dist = null;
          let distText = "Distanca e panjohur";
          if (userLocation) {
            dist = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              coords.lat,
              coords.lng
            );
            distText = `${dist.toFixed(1)} km larg`;
          }

          return {
            id: docSnap.id,
            ...data,
            photoUrl,
            position: { lat: coords.lat, lng: coords.lng },
            distance: dist,
            distanceText: distText,
          };
        })
      );

      const clean = rows.filter(Boolean);
      if (userLocation) {
        clean.sort(
          (a, b) =>
            (a.distance ?? Infinity) - (b.distance ?? Infinity)
        );
      }
      setDoctors(clean);
    } catch (e) {
      console.error("Error fetching doctors for map:", e);
      setError("Nuk mund të ngarkojmë mjekët në hartë.");
    } finally {
      setMapLoading(false);
    }
  }, [userLocation]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  /* ---------- filters (useMemo always called) ---------- */
  const availableDoctorSpecialties = useMemo(
    () =>
      [...new Set(doctors.map((d) => d.specialty).filter(Boolean))].sort(),
    [doctors]
  );

  const filteredDoctors = useMemo(() => {
    let filtered = doctors.filter((d) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        d.name?.toLowerCase().includes(q) ||
        d.specialty?.toLowerCase().includes(q) ||
        d.location?.address?.toLowerCase().includes(q);

      const matchesSpecialty =
        !specialtyFilter || d.specialty === specialtyFilter;

      return matchesSearch && matchesSpecialty;
    });

    if (userLocation && nearbyRadius > 0) {
      filtered = filtered.filter(
        (d) => !d.distance || d.distance <= nearbyRadius
      );
    }

    return filtered;
  }, [doctors, searchTerm, specialtyFilter, userLocation, nearbyRadius]);

  /* ---------- callbacks (always defined) ---------- */
  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSpecialtyFilter("");
    setNearbyRadius(25);
  }, []);

  const handleDoctorClick = useCallback(
    (id) => navigate(`/doctor/${id}`),
    [navigate]
  );

  const toggleFollowDoctor = useCallback(
    async (doctorId) => {
      if (!user) return;
      const isFollowing = !!followedDoctors[doctorId];

      setFollowLoading((p) => ({ ...p, [doctorId]: true }));
      setFollowedDoctors((p) => ({ ...p, [doctorId]: !isFollowing }));

      try {
        // TODO: implement follow/unfollow logic with Firestore if you want
        // (user doc + doctor doc followerCount)
      } catch (e) {
        console.error("Failed to toggle follow:", e);
        // revert optimistic
        setFollowedDoctors((p) => ({
          ...p,
          [doctorId]: isFollowing,
        }));
      } finally {
        setFollowLoading((p) => ({ ...p, [doctorId]: false }));
      }
    },
    [user, followedDoctors]
  );

  /* ---------- auth guard (AFTER hooks) ---------- */
  if (!user) {
    return (
      <div
        style={{
          padding: 16,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 14,
          color: "#4B5563",
        }}
      >
        Duhet të hysh në llogarinë tënde për të parë hartën e mjekëve.
      </div>
    );
  }

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <div
        style={{
          padding: 16,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                border: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#FFFFFF",
                cursor: "pointer",
              }}
            >
              <ArrowLeft
                style={{ width: 18, height: 18, color: "#4B5563" }}
              />
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0F172A",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <MapPin
                  style={{ width: 18, height: 18, color: "#0F766E" }}
                />
                Harta e mjekëve
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                Gjej mjekë dhe klinika pranë teje, sipas specialitetit dhe
                vendndodhjes.
              </span>
            </div>

            <div style={{ flex: 1 }} />
          </div>
        </header>

        {/* Filters */}
        <div className="mb-3">
          <SearchFilters
            activeTab="map"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            specialtyFilter={specialtyFilter}
            setSpecialtyFilter={setSpecialtyFilter}
            availableSpecialties={availableDoctorSpecialties}
            nearbyRadius={nearbyRadius}
            setNearbyRadius={setNearbyRadius}
            userLocation={userLocation}
            onClearFilters={handleClearFilters}
          />
        </div>

        {/* Location status */}
        <div className="mb-3">
          <LocationStatus
            userLocation={userLocation}
            isGettingLocation={isGettingLocation}
            locationError={locationError}
            nearbyRadius={nearbyRadius}
            onEnableLocation={getUserLocation}
          />
        </div>

        {/* Map card */}
        <div
          style={{
            borderRadius: 18,
            border: "1px solid #E5E7EB",
            background: "#FFFFFF",
            boxShadow: "0 10px 30px -18px rgba(15,23,42,0.25)",
            overflow: "hidden",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid #E5E7EB",
              background:
                "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(236,252,245,0.95))",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0F172A",
              }}
            >
              Mjekët në hartë
            </span>
            <span
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.4)",
                background: "rgba(248,250,252,0.9)",
                color: "#475569",
                whiteSpace: "nowrap",
              }}
            >
              {filteredDoctors.length === 0
                ? "Asnjë mjek"
                : filteredDoctors.length === 1
                ? "1 mjek"
                : `${filteredDoctors.length} mjekë`}
            </span>
          </div>

          <div style={{ height: 360 }}>
            <MapView
              isMapLoaded={isMapLoaded}
              mapCenter={mapCenter}
              doctors={filteredDoctors}
              userLocation={userLocation}
              nearbyRadius={nearbyRadius}
              mapLoading={mapLoading}
              onDoctorClick={handleDoctorClick}
              onToggleFollowDoctor={toggleFollowDoctor}
              followedDoctors={followedDoctors}
              followLoading={followLoading}
              GOOGLE_MAPS_API_KEY={GOOGLE_MAPS_API_KEY}
              onChangeRadius={(r) => setNearbyRadius(r)}
              onNearMe={getUserLocation}
              onSearchArea={(center) => {
                setMapCenter(center);
                setUserLocation(center);
              }}
              onBack={() => navigate("/patient")}
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              fontSize: 13,
              color: "#B91C1C",
              marginBottom: 8,
            }}
          >
            {error}
          </div>
        )}

        {!mapLoading && filteredDoctors.length === 0 && !error && (
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              border: "1px dashed #CBD5F5",
              background: "#F9FAFB",
              fontSize: 13,
              color: "#6B7280",
            }}
          >
            Nuk u gjet asnjë mjek për filtrat aktualë. Provo të rrisësh
            rrezen, të ndryshosh specialitetin ose të pastrosh filtrat.
          </div>
        )}
      </div>
    </div>
  );
}
